import _ from 'lodash'
import React from 'react'
import { Box, Link, Pagination, Skeleton, Stack, Typography } from '@mui/material'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { APP_BASE_URL, DIARY, DIARY_URL } from '../../constants'
import BodyHeader from '../molecules/BodyHeader'
import EventCard from '../molecules/EventCard'
import Zone from '../organisms/Zone'
import Filtering from '../molecules/Filtering'
import useDairy from '../../hooks/useDairy'
import useEvents from '../../hooks/useEvents'

const Diary = () => {
	const { isLoading: isDairyLoading, title, content } = useDairy()

	let { pageNumber } = useParams()
	const numberPerPage = 4

	pageNumber = pageNumber || 1

	const { events: pastEvents } = useEvents('publish', pageNumber, numberPerPage)
	const { isLoading: isFutureEventLoading, events: futureEvents } = useEvents('future', pageNumber)

	const navigate = useNavigate()

	const handleChange = (event, value) => {
		navigate(`${APP_BASE_URL}${DIARY_URL}/${value}`)
	}

	const { hash } = useLocation()
	const filterName = hash && hash.split('#')[1]

	const futureEventsFiltering =
		(filterName &&
			_.filter(futureEvents?.list, ({ categoryName }) => categoryName === filterName)) ||
		futureEvents?.list

	return (
		<>
			{(isDairyLoading && <Skeleton />) || <BodyHeader title={title}>{content}</BodyHeader>}
			<Zone color="ivory">
				<Typography variant="h3" color="secondary" sx={{ fontSize: 16, fontWeight: 900 }}>
					{DIARY}
				</Typography>
				<Typography variant="h4" color="text.primary" sx={{ fontSize: 32, fontWeight: 700 }}>
					Participez à nos prochains événements
				</Typography>
				{/* <Box sx={{ display: 'flex', justifyContent: 'center', m: theme.spacing(8, 0, 2) }}>
					<Filtering
						label="Ateliers"
						list={['Atelier couture', 'Atelier tapisserie', 'Atelier récup’']}
					/>
					<Filtering label="Vente" />
					<Filtering label="Événements" />
				</Box> */}
				{futureEvents?.list && (
					<Box
						sx={(theme) => ({
							display: 'flex',
							justifyContent: 'center',
							m: (theme) => theme.spacing(8, 9.5, 2),
							[theme.breakpoints.down('md')]: {
								mx: 0,
							},
						})}
					>
						<Filtering
							label="Filtrer par catégorie d’atelier"
							list={futureEvents?.list}
							filterName={filterName}
						/>
					</Box>
				)}
				<Stack
					sx={(theme) => ({
						m: (theme) => theme.spacing(4, 'auto', 2),
						justifyContent: 'center',
						[theme.breakpoints.down('md')]: {
							direction: 'column',
							flexWrap: 'wrap',
							p: 0,
							m: 0,

							'& .MuiPaper-root': {
								m: 2,
							},
						},
					})}
					direction="row"
					spacing={4}
				>
					{(isFutureEventLoading && <Skeleton />) ||
						(futureEventsFiltering &&
							futureEventsFiltering.map(
								({ title, date, hour, description, categoryName, link, image, id }, index) =>
									index < 4 && (
										<>
											{(link && (
												<Link href={link} underline="none" target="_blank" key={id}>
													<EventCard
														bg="white"
														color="text.primary"
														image={image}
														title={date}
														hour={hour}
														subtitle={title}
														body={description}
														eventType={categoryName}
													/>
												</Link>
											)) || (
												<EventCard
													bg="white"
													color="text.primary"
													image={image}
													title={date}
													hour={hour}
													subtitle={title}
													body={description}
													eventType={categoryName}
												/>
											)}
										</>
									),
							))}
				</Stack>
				{futureEventsFiltering && futureEventsFiltering.length > 0 && (
					<Stack
						sx={(theme) => ({
							m: (theme) => theme.spacing(4, 'auto', 2),
							justifyContent: 'center',
							[theme.breakpoints.down('md')]: {
								direction: 'column',
								flexWrap: 'wrap',
								p: 0,
								m: 0,

								'& .MuiPaper-root': {
									m: 2,
								},
							},
						})}
						direction="row"
						spacing={4}
					>
						{futureEventsFiltering &&
							futureEventsFiltering.map(
								({ title, date, hour, description, categoryName, link, image, id }, index) =>
									index > 3 &&
									index < 8 && (
										<>
											{(link && (
												<Link href={link} underline="none" target="_blank" key={id}>
													<EventCard
														bg="white"
														color="text.primary"
														image={image}
														title={date}
														hour={hour}
														subtitle={title}
														body={description}
														eventType={categoryName}
													/>
												</Link>
											)) || (
												<EventCard
													bg="white"
													color="text.primary"
													image={image}
													title={date}
													hour={hour}
													subtitle={title}
													body={description}
													eventType={categoryName}
												/>
											)}
										</>
									),
							)}
					</Stack>
				)}
				{futureEvents?.infos?.totalPages && futureEvents?.infos?.totalPages > 1 && (
					<Pagination
						count={parseInt(futureEvents?.infos?.totalPages)}
						page={parseInt(pageNumber)}
						onChange={handleChange}
						sx={{
							display: 'flex',
							justifyContent: 'center',
							'& .MuiPagination-ul': {
								borderBottom: '2px solid black',
								pb: 2,

								'& .MuiButtonBase-root': {
									fontSize: 16,
									fontWeight: 600,

									'&.Mui-selected': {
										bgcolor: 'transparent',
										color: 'secondary.main',
										fontSize: 18,
										fontWeight: 900,
									},
								},
							},
						}}
					/>
				)}
			</Zone>
			<Zone color="black">
				<Typography variant="h3" color="secondary" sx={{ fontSize: 16, fontWeight: 900 }}>
					Passés
				</Typography>
				<Typography variant="h4" color="white" sx={{ fontSize: 32, fontWeight: 700 }}>
					Ils ont déjà eu lieu
				</Typography>

				<Box
					sx={{
						oveflowX: 'scroll',
						maxWidth: 1200,
						justifyContent: 'center',
						m: (theme) => theme.spacing(0, 'auto'),
					}}
				>
					<Stack
						sx={(theme) => ({
							m: theme.spacing(4, 'auto', 2),
							overflowX: 'scroll',
						})}
						direction="row"
						justifyContent="flex-start"
						spacing={4}
					>
						{pastEvents?.list &&
							pastEvents?.list.map(
								({ title, date, hour, description, categoryName, lien, image, id }) => (
									<Link href={lien} underline="none" target="_blank" key={id}>
										<EventCard
											bg="text.greyLight"
											image={image}
											title={date}
											hour={hour}
											subtitle={title}
											body={description}
											eventType={categoryName}
										/>
									</Link>
								),
							)}
					</Stack>
				</Box>
			</Zone>
		</>
	)
}

export default Diary
